import type { Question } from '../../types';

export class ValidationService {
  static validateQuestion(question: Question, answer: string | string[] | undefined | null): { isValid: boolean; message?: string } {
    // Static text and section questions don't require validation
    if (question.type === 'static' || question.type === 'section') {
      return { isValid: true };
    }

    if (question.required && this.isAnswerEmpty(answer)) {
      return { isValid: false, message: 'This question is required' };
    }

    return this.validateByType(question, answer);
  }

  private static isAnswerEmpty(answer: string | string[] | undefined | null): boolean {
    return answer === null || 
           answer === undefined || 
           (Array.isArray(answer) && answer.length === 0) || 
           answer === '';
  }

  private static validateByType(question: Question, answer: string | string[] | undefined | null): { isValid: boolean; message?: string } {
    if (!answer || typeof answer !== 'string') {
      return { isValid: true };
    }

    const validators = {
      email: () => this.validateEmail(answer),
      number: () => this.validateNumber(answer, question),
      rating: () => this.validateRating(answer, question),
      date: () => this.validateDate(answer),
      grid: () => this.validateGrid(answer, question),
    };

    const validator = validators[question.type as keyof typeof validators];
    return validator ? validator() : { isValid: true };
  }

  private static validateEmail(email: string): { isValid: boolean; message?: string } {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) 
      ? { isValid: true }
      : { isValid: false, message: 'Please enter a valid email address' };
  }

  private static validateNumber(value: string, question: Question): { isValid: boolean; message?: string } {
    const num = parseFloat(value);

    if (isNaN(num)) {
      return { isValid: false, message: 'Please enter a valid number' };
    }

    // Check validation.rules first, then fall back to question.min/max for backward compatibility
    const min = question.validation?.rules?.min ?? question.min;
    const max = question.validation?.rules?.max ?? question.max;

    if (min !== undefined && num < min) {
      return {
        isValid: false,
        message: question.validation?.errorMessage || `Value must be at least ${min}`
      };
    }

    if (max !== undefined && num > max) {
      return {
        isValid: false,
        message: question.validation?.errorMessage || `Value must be at most ${max}`
      };
    }

    return { isValid: true };
  }

  private static validateRating(value: string, question: Question): { isValid: boolean; message?: string } {
    const rating = parseInt(value);
    const min = question.min || 1;
    const max = question.max || 5;
    
    return (rating >= min && rating <= max)
      ? { isValid: true }
      : { isValid: false, message: `Rating must be between ${min} and ${max}` };
  }

  private static validateDate(value: string): { isValid: boolean; message?: string } {
    const date = new Date(value);
    return !isNaN(date.getTime())
      ? { isValid: true }
      : { isValid: false, message: 'Please enter a valid date' };
  }

  private static validateGrid(answer: string, question: Question): { isValid: boolean; message?: string } {
    if (!question.required) {
      return { isValid: true };
    }

    try {
      const answers = JSON.parse(answer);
      const expectedRows = question.rows || [];

      for (let i = 0; i < expectedRows.length; i++) {
        if (!answers[i.toString()]) {
          return { isValid: false, message: 'Please answer all required questions' };
        }
      }

      return { isValid: true };
    } catch {
      return { isValid: false, message: 'Invalid grid answer format' };
    }
  }
}
